export type PackageDefinition = {
  id: string;
  title: string;
  strapline: string;
  whyItMatters: string;
  workflows: string[];
  integrations: string[];
  deliverables: string[];
};

export type SorMockCall = {
  id: string;
  title: string;
  system: string;
  method: "GET" | "POST";
  endpoint: string;
  purpose: string;
  packageIds: string[];
  request: Record<string, unknown>;
  response: Record<string, unknown>;
};

export const CONFIGURABLE_PACKAGES: PackageDefinition[] = [
  {
    id: "sba_servicing",
    title: "SBA Servicing",
    strapline: "For banks that originate, sell, board, or outsource SBA servicing.",
    whyItMatters: "Converts SBA production into an operating view that tracks sold balances, servicing assets, guaranty status, fee income, exception queues, and lender service provider activity.",
    workflows: [
      "Servicing asset valuation and gain-on-sale tracking",
      "SBA guaranty lifecycle and exception monitoring",
      "Boarding, transfer, and payment remittance workflows",
    ],
    integrations: ["Core loan system", "SBA servicing platform / LSP", "CRM / pipeline"],
    deliverables: ["Servicing dashboard", "Exception queue", "Secondary-market reporting"],
  },
  {
    id: "cdfi_cra",
    title: "CDFI / CRA Plan",
    strapline: "For institutions running CRA strategic plans, CDFI grants, or reinvestment programs.",
    whyItMatters: "Ties lending, outreach, grants, and impact metrics together so management can see whether community development activity is on plan and examiner-ready at all times.",
    workflows: [
      "Strategic plan milestone tracking",
      "LMI lending, outreach, and impact scorecards",
      "Grant allocation and community development evidence management",
    ],
    integrations: ["HMDA / CRA datasets", "CRM / outreach system", "Document vault"],
    deliverables: ["CRA heat maps", "CDFI impact ledger", "Exam-ready evidence binder"],
  },
  {
    id: "exchange_1031",
    title: "1031 Exchange",
    strapline: "For banks offering qualified intermediary or exchange deposit services.",
    whyItMatters: "Adds operational control around segregated exchange balances, wire disbursements, authorization rules, and timeline milestones that standard commercial dashboards do not capture.",
    workflows: [
      "Exchange account setup and segregation controls",
      "Disbursement approval and wire verification",
      "45-day / 180-day milestone monitoring",
    ],
    integrations: ["Core deposits", "Treasury / wire platform", "Exchange case ledger"],
    deliverables: ["Exchange dashboard", "Approval workflow", "Milestone alerts"],
  },
  {
    id: "nmtc",
    title: "NMTC",
    strapline: "For community development entities and project finance programs.",
    whyItMatters: "Creates a single operating layer for pipeline, project qualification, disbursement stages, compliance artifacts, and impact reporting across long-lived NMTC transactions.",
    workflows: [
      "Project qualification and underwriting checklist",
      "Disbursement staging and compliance evidence collection",
      "Impact, jobs, and covenant tracking",
    ],
    integrations: ["CRM / project pipeline", "Document vault", "General ledger / funding ledger"],
    deliverables: ["Deal pipeline", "Compliance room", "Impact reporting pack"],
  },
  {
    id: "ag_guarantees",
    title: "Ag Government Guarantees",
    strapline: "For banks using USDA, FSA, Farmer Mac, or similar guarantee programs.",
    whyItMatters: "Brings guaranteed ag lending into the same risk and portfolio view as the rest of the bank, while preserving guarantee-specific collateral, eligibility, and reporting requirements.",
    workflows: [
      "Guarantee eligibility and policy exception tracking",
      "Collateral package and borrowing-base monitoring",
      "Annual review, covenant, and insurance certification workflows",
    ],
    integrations: ["Core loans", "USDA / FSA adapters", "Farmer Mac package workflow"],
    deliverables: ["Guaranteed portfolio view", "Eligibility checklist", "Collateral packet tracker"],
  },
  {
    id: "market_expansion",
    title: "Local Market Expansion",
    strapline: "For banks opening new branches, LPOs, or production teams in targeted geographies.",
    whyItMatters: "Provides a repeatable way to measure whether a new market is producing deposits, loans, treasury adoption, and relationship depth fast enough to justify expansion.",
    workflows: [
      "Market scorecards by branch, team, or region",
      "Relationship-manager production tracking",
      "Competitor disruption and treasury penetration analysis",
    ],
    integrations: ["Core branch performance", "CRM / banker pipeline", "Market intelligence feeds"],
    deliverables: ["Expansion scorecard", "RM league table", "Market-entry dashboard"],
  },
];

export const SOR_MOCK_CALLS: SorMockCall[] = [
  {
    id: "sba-sync",
    title: "Sync SBA booked loans into servicing operations",
    system: "Core loan system -> SBA servicing package",
    method: "POST",
    endpoint: "/api/integrations/fis-horizon/sba-servicing/sync",
    purpose: "Pull newly booked or sold SBA loans from the core, enrich with guaranty and sale metadata, and stage them for servicing and secondary-market reporting.",
    packageIds: ["sba_servicing"],
    request: {
      runDate: "2026-04-17",
      institutionId: "mission-valley-bank",
      filters: {
        programs: ["SBA 7A", "SBA 504"],
        statuses: ["Booked", "Sold", "Transferred"],
        asOfDate: "2026-04-16",
      },
    },
    response: {
      syncId: "sba-sync-20260417-001",
      importedLoans: 18,
      soldBalance: 43600000,
      servicingAssetDelta: 214000,
      exceptions: [
        { loanNumber: "7A-44192", issue: "Missing SBA guaranty purchase status" },
      ],
    },
  },
  {
    id: "cra-impact",
    title: "Refresh CRA and CDFI impact metrics",
    system: "CRM / outreach + HMDA / CRA datasets",
    method: "POST",
    endpoint: "/api/integrations/salesforce-fsc/cdfi-cra/impact-refresh",
    purpose: "Combine booked loan activity, outreach events, grants, and tract-level impact measures into a single CRA / CDFI strategic-plan scorecard.",
    packageIds: ["cdfi_cra"],
    request: {
      planId: "cra-strategic-plan-2026",
      refreshWindow: {
        start: "2026-01-01",
        end: "2026-04-17",
      },
      include: ["lmi_loans", "community_events", "grants", "tract_gaps"],
    },
    response: {
      refreshId: "cra-20260417-014",
      planProgressPct: 63,
      lmiLoanCount: 41,
      outreachEventsLogged: 19,
      tractAlerts: [
        { tract: "06037101110", issue: "No originations against quarterly target" },
      ],
    },
  },
  {
    id: "exchange-balances",
    title: "Export 1031 exchange account balances and milestones",
    system: "Core deposits / exchange ledger",
    method: "POST",
    endpoint: "/api/integrations/core-deposits/1031/exchanges/export",
    purpose: "Retrieve segregated exchange balances, milestone dates, and authorization metadata for active 1031 files.",
    packageIds: ["exchange_1031"],
    request: {
      asOfDate: "2026-04-17",
      includeInactive: false,
      balanceThreshold: 100000,
    },
    response: {
      batchId: "exchg-20260417-002",
      activeExchanges: 27,
      aggregateBalances: 118400000,
      approachingDeadlines: [
        { exchangeId: "1031-8841", milestone: "45-day identification", dueDate: "2026-04-21" },
      ],
    },
  },
  {
    id: "exchange-wire-verify",
    title: "Verify 1031 disbursement before wire release",
    system: "Treasury / wire platform",
    method: "POST",
    endpoint: "/api/integrations/treasury-wires/1031/disbursement-verify",
    purpose: "Validate that a pending exchange wire has dual authorization, correct beneficiary data, and an approved exchange instruction before release.",
    packageIds: ["exchange_1031"],
    request: {
      wireId: "WIRE-991244",
      exchangeId: "1031-8841",
      beneficiary: {
        name: "Coastal Title Co.",
        accountLast4: "4491",
      },
      requestedAmount: 2850000,
    },
    response: {
      decision: "approved_with_hold",
      holdReason: "Awaiting second signer confirmation",
      matchedInstruction: true,
      releaseWindow: "2026-04-17T14:30:00-07:00",
    },
  },
  {
    id: "nmtc-pipeline",
    title: "Upsert NMTC project pipeline from CRM",
    system: "CRM / project pipeline",
    method: "POST",
    endpoint: "/api/integrations/crm/nmtc/projects/upsert",
    purpose: "Promote qualified community development opportunities from CRM into the NMTC operating workspace with stage, census-tract, and impact fields.",
    packageIds: ["nmtc"],
    request: {
      sourceSystem: "salesforce-fsc",
      projects: [
        {
          crmId: "OPP-22104",
          borrowerName: "Central Valley Health Campus",
          requestedAllocation: 9500000,
          lowIncomeCommunityTract: "06029004201",
          targetJobs: 58,
        },
      ],
    },
    response: {
      upserted: 1,
      workspaceIds: ["nmtc-opp-22104"],
      complianceChecklistStatus: "initialized",
    },
  },
  {
    id: "ag-eligibility",
    title: "Check ag guarantee eligibility package",
    system: "USDA / FSA adapter",
    method: "POST",
    endpoint: "/api/integrations/usda-fsa/ag-guarantees/eligibility-check",
    purpose: "Validate borrower, collateral, and purpose data against configured guarantee-program rules before credit committee approval.",
    packageIds: ["ag_guarantees"],
    request: {
      borrowerId: "AGR-11082",
      programs: ["USDA_BI", "FSA_GUARANTEED"],
      loanRequest: {
        amount: 6200000,
        purpose: "working_capital_and_equipment",
        collateralType: "crop_and_real_estate",
      },
    },
    response: {
      eligiblePrograms: ["USDA_BI"],
      ineligiblePrograms: [
        { program: "FSA_GUARANTEED", reason: "Borrower revenue exceeds current threshold" },
      ],
      requiredDocuments: ["Borrowing base", "Insurance certificate", "Environmental questionnaire"],
    },
  },
  {
    id: "farmer-mac-packet",
    title: "Submit Farmer Mac collateral package",
    system: "Farmer Mac package workflow",
    method: "POST",
    endpoint: "/api/integrations/farmer-mac/collateral-packages/submit",
    purpose: "Package appraisals, insurance, collateral schedules, and covenant data for guaranteed ag credits requiring secondary review.",
    packageIds: ["ag_guarantees"],
    request: {
      packageId: "fm-20260417-003",
      borrowerId: "AGR-11082",
      collateralSummary: {
        appraisedValue: 11400000,
        insuredValue: 9200000,
        lienPosition: "first",
      },
      artifacts: ["appraisal.pdf", "insurance-cert.pdf", "borrowing-base.xlsx"],
    },
    response: {
      submissionStatus: "accepted",
      trackingNumber: "FM-ACK-884291",
      followUpDue: "2026-04-24",
    },
  },
  {
    id: "market-scorecard",
    title: "Run local market expansion scorecard",
    system: "Core branch performance + CRM + market data",
    method: "POST",
    endpoint: "/api/integrations/market-intel/expansion-scorecard/run",
    purpose: "Generate a market-entry scorecard that combines branch production, banker pipelines, treasury adoption, and competitor disruption signals.",
    packageIds: ["market_expansion"],
    request: {
      marketId: "santa-cruz-ca",
      asOfDate: "2026-04-17",
      metrics: ["deposits", "loanProduction", "treasuryAdoption", "newRelationships", "competitorMoves"],
    },
    response: {
      scorecardId: "mkt-20260417-sc",
      overallScore: 78,
      strengths: ["Treasury adoption above plan", "Middle-market deposit wins"],
      risks: ["CRE pipeline below target", "One competitor retention campaign detected"],
    },
  },
];
